import React, { useLayoutEffect, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

// Styles
import {
  AdviceContainer,
  ButtonContainer,
  ContainerError,
  GeneralContainer,
  TitleContainerWithButton,
} from '../styles';

// Components
import { Typography } from '../../../display/Typography';
import { MaterialSelectionTable } from '../../../display/tables/formTables/MaterialsSelectionTable';
import SimpleBackgroundSwitch from '../../../inputs/buttons/SwitchButton/SimpleBackground';
import { MainButton } from '../../../inputs/buttons/MainButton';
import { ActionWizardFooter } from '../ActionWizardFooter';

// Types
import { type MaterialFormValues, type MaterialType } from './types';
import { type dataTable } from '../../../display/tables/formTables/MaterialsSelectionTable/types';
import { Materials } from '../MachineryForm/types';

// Utils
import { useCacheFormInSessionStorage } from '../../../../hooks/useCacheForm';
import { handleAdd, handleDelete, handleUpdate } from '../formTableUtils';
import {
  compressSessionStorage,
  decompressSessionStorage,
} from '../../../../utils/sessionStorage';
import { DataState } from '../types';
import { OrderState } from '../../../../types/orders';

const MaterialsForm = ({
  initialData,
  onFormSubmit,
  sessionStorageKey,
  updateKey,
  textButton,
  isTerminalStep,
  showSaveButton,
  saveButtonText,
  onBeforeSave,
  showValidateButton,
  onBeforeValidate,
  showPendingCertificationButton,
  pendingCertificationButtonText,
  onBeforePendingCertification,
}: MaterialType) => {
  const { t } = useTranslation(['forms', 'common']);
  // ✅ Para activar validación visual al navegar desde modal de errores
  const locationState = useLocation();
  const { control, handleSubmit, setValue, register, getValues } =
    useForm<MaterialFormValues>({
      defaultValues: {
        ...initialData,
        // ✅ FIX: Default a 'false' (No hay) para NewAction
        // Si initialData tiene valor (ActionsValidation o sessionStorage), se usará ese
        isMaterialTableDisplayed: initialData.isMaterialTableDisplayed
          ? initialData.isMaterialTableDisplayed
          : 'false',
      },
    });
  const [dataFromTables, setDataFromTables] = useState<null | dataTable[]>(
    null
  );
  // ✅ FIX: Default a 'false' (No hay) para NewAction - si hay datos de servidor (ActionsValidation) se sobrescribirá
  const [switchState, setSwitchState] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const updateTableKey = 'materialsData';
  useCacheFormInSessionStorage(sessionStorageKey, control);

  // ✅ Activar validación visual al navegar desde modal de errores
  useEffect(() => {
    if (locationState.state?.validateOnMount && switchState) {
      console.log('🚨 MaterialsForm: Triggering validation on mount');
      const tableValues = getValues('materialsTable');
      validateMaterialsTable(tableValues as never[]);
    }
  }, [locationState.state?.validateOnMount, switchState]);

  const switchStateValue = () => {
    const { isMaterialTableDisplayed } = initialData;

    if (isMaterialTableDisplayed) {
      return isMaterialTableDisplayed === 'true';
    }

    // ✅ FIX: Default a 'false' (No hay) para NewAction
    setValue('isMaterialTableDisplayed', 'false');
    return false;
  };

  useLayoutEffect(() => {
    setDataFromTables(Object.values(initialData?.materialsTable ?? {}));
    setSwitchState(switchStateValue());
  }, []);

  const handleChange = () => {
    if (switchState) {
      setSwitchState(false);
      setValue('isMaterialTableDisplayed', 'false');
      setValue('materialsTable', null);
      if (updateKey) {
        const idsToDelete = Object.values(
          initialData?.materialsTable ?? {}
        ).map((item: any) => item.materialUnitId);

        const currentCacheData = sessionStorage.getItem(updateKey)
          ? JSON.parse(
              decompressSessionStorage(
                sessionStorage.getItem(updateKey) as string
              )
            )
          : {};

        currentCacheData[updateTableKey] ??= {};
        currentCacheData[updateTableKey][DataState.deleted] = idsToDelete;
        currentCacheData[updateTableKey][DataState.added] = [];
        currentCacheData[updateTableKey][DataState.updated] = [];

        sessionStorage.setItem(
          updateKey,
          compressSessionStorage(JSON.stringify(currentCacheData))
        );
      }
      setDataFromTables([]);
    } else {
      setSwitchState(true);
      setValue('isMaterialTableDisplayed', 'true');
    }
  };

  const validateMaterialsTable = (tableValues: Materials[]) => {
    const existAtLeastOneValue = Object.entries(tableValues ?? {}).some(
      ([, element]) => !!element
    );

    const allTheElementsHasUnits = Object.entries(tableValues ?? {}).every(
      ([, element]) =>
        element === undefined ? true : !!element && Number(element.ut) > 0
    );

    !existAtLeastOneValue
      ? setError('Afegiu almenys un element a la taula principal')
      : setError('Si us plau, ompliu tots els camps dels elements afegits');

    return existAtLeastOneValue && allTheElementsHasUnits;
  };

  const checkTableValues = () => {
    if (switchState) {
      const tableValues = getValues('materialsTable');
      return validateMaterialsTable(tableValues as never[]);
    }

    return true;
  };

  const handleUpdateItem = (
    eventValue: string,
    id: string,
    unitId: string,
    completeId: string
  ) => {
    setValue(completeId as keyof MaterialFormValues, eventValue);

    if (!updateKey) {
      return;
    }

    handleUpdate(
      eventValue,
      id,
      unitId,
      initialData,
      updateTableKey,
      'materialsTable',
      updateKey
    );
  };

  const handleAddItem = (id: string) => {
    if (updateKey) {
      handleAdd(id, updateTableKey, updateKey);
    }
  };
  const handleDeleteItem = (id: string, unitId: string) => {
    if (!updateKey) {
      return;
    }

    handleDelete(id, unitId, updateTableKey, updateKey);
  };
  return (
    <GeneralContainer
      onSubmit={(e) => {
        if (checkTableValues()) {
          handleSubmit(onFormSubmit)(e);
        } else {
          const tableValues = getValues('materialsTable');
          validateMaterialsTable(tableValues as never[]);
          e.preventDefault();
        }
      }}
    >
      <TitleContainerWithButton>
        <Typography variant="semiBold" component="h3" size="L">
          {t('materials.title')}
        </Typography>
        <SimpleBackgroundSwitch
          changeState={handleChange}
          state={switchState}
          disabled={
            initialData?.parentExpeditionOrderStatus === OrderState.Annulled ||
            initialData?.parentExpeditionOrderStatus === OrderState.End ||
            initialData?.state === OrderState.Annulled
          }
        />
      </TitleContainerWithButton>
      {switchState && dataFromTables ? (
        <MaterialSelectionTable
          data={initialData.materialsData}
          register={register}
          setValue={setValue}
          defaultValues={dataFromTables}
          onUpdate={handleUpdateItem}
          onDelete={updateKey ? handleDeleteItem : undefined}
          onAdd={updateKey ? handleAddItem : undefined}
        />
      ) : (
        <AdviceContainer>
          <Typography component="p" variant="semiBold" colorText="greyDark">
            {t('materials.noMaterials')}
          </Typography>
        </AdviceContainer>
      )}
      {error && switchState && (
        <ContainerError>
          <Typography component="p" size="S" colorText="lightCoralRed">
            {error}
          </Typography>
        </ContainerError>
      )}
      {isTerminalStep ? (
        // Actuació bàsica: Materials es el último paso del asistente, así que
        // lleva el pie completo (Guardar / Validar / Pendent de certificar).
        <ActionWizardFooter
          textButton={textButton}
          showSaveButton={showSaveButton}
          saveButtonText={saveButtonText}
          onBeforeSave={onBeforeSave}
          showValidateButton={showValidateButton}
          onBeforeValidate={onBeforeValidate}
          showPendingCertificationButton={showPendingCertificationButton}
          pendingCertificationButtonText={pendingCertificationButtonText}
          onBeforePendingCertification={onBeforePendingCertification}
          parentExpeditionOrderStatus={initialData?.parentExpeditionOrderStatus}
          state={initialData?.state}
        />
      ) : (
        <ButtonContainer>
          <MainButton
            text={
              initialData?.parentExpeditionOrderStatus ===
                OrderState.Annulled ||
              initialData?.parentExpeditionOrderStatus === OrderState.End ||
              initialData?.state === OrderState.Annulled
                ? t('common:buttons.continue')
                : textButton || t('common:buttons.continue')
            }
            type="submit"
          />
        </ButtonContainer>
      )}
    </GeneralContainer>
  );
};

export default MaterialsForm;
